// essentials
import PropTypes from 'prop-types';
import React from 'react';
import { withStyles, withTheme } from '@material-ui/core/styles';

// modules
import throttle from 'lodash.throttle';

// ui / ux
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';

// icons

// ours
import { uuidv4 } from '../common';

const styles = (theme) => ({
  textfield: {
    width: '100%',
    marginRight: theme.spacing(2),
    color: 'white !important',
    '& .MuiFormLabel-root.Mui-focused': {
      color: theme.palette.text.secondary
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary
    }
  },
  tooltip: {
    color: 'red'
  },
  paper: {
    '& > ul': {
      maxHeight: '65vh'
    }
  }
});

class GeoAutocomplete extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      id: `geo-ac-${uuidv4()}`,
      loading: false,
      text: '',
      opts: [],
      dynOpts: [],
      error: '',
      errorOpen: false
    };

    this.onSelect = this.onSelect.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onErrorClose = this.onErrorClose.bind(this);
    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.updateOpts = this.updateOpts.bind(this);
    this.updateOptsThrottled = throttle(this.updateOpts, 550, {
      trailing: true,
      leading: true
    });

    this._lastDataReq = 0;

    this._domRef = React.createRef();
    this._reLatLon = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/;
  }

  showError(error) {
    this.setState({
      error: `⚠️  ${error}`,
      errorOpen: true
    });
  }

  onSelect(_, v) {
    this._lastDataReq = 0;
    this.setState({
      text: '',
      opts: [],
      dynOpts: [],
      loading: false
    });

    this.props.onSelect(v);
  }

  dynamicSearchOptions(v) {
    const { withDynamicSearch, withLatLonSearch } = this.props;
    const dynamicOpts = [];
    if (withDynamicSearch) {
      dynamicOpts.push({
        type: 'dynamic',
        action: 'near-here',
        name: `Search '${v}' near here`,
        val: v
      });
    }
    if (withLatLonSearch && v.match(this._reLatLon)) {
      dynamicOpts.push({
        type: 'dynamic',
        action: 'zoom-to',
        name: `Zoom to ${v}`,
        val: v
      });
    }
    return dynamicOpts;
  }
  updateOpts(v) {
    const { dataFn } = this.props;

    const now = new Date().getTime();
    this._lastDataReq = now;

    dataFn(v, (e, r) => {
      if (now === this._lastDataReq) {
        this.setState({
          opts: r || [],
          loading: false
        });
      }
    });
  }

  onChange(e) {
    const { dataFnMinLength } = this.props;
    const { value } = e.target;

    if (value.length <= dataFnMinLength) {
      return this.setState({
        loading: false,
        dynOpts: [],
        opts: [],
        text: value
      });
    }

    // real time changes
    this.setState({
      text: value,
      opts: [],
      dynOpts: this.dynamicSearchOptions(value),
      loading: value.length > dataFnMinLength
    });

    this.updateOptsThrottled(value);
  }

  onErrorClose() {
    this.setState({
      error: '',
      errorOpen: false
    });
  }

  onOpen() {
    this.onErrorClose();

    if (this.props.onOpen) {
      this.props.onOpen();
    }
  }

  onClose() {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  render() {
    const { opts, dynOpts, loading, text, id } = this.state;
    const { classes, placeholder, label, popperPlacement } = this.props;

    return (
      <ClickAwayListener onClickAway={this.onErrorClose}>
        <div>
          <Tooltip
            className={classes.tooltip}
            PopperProps={{
              disablePortal: true
            }}
            onClose={this.onErrorClose}
            open
            arrow
            placement="bottom"
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={this.state.error}
          >
            <Autocomplete
              PopperComponent={popperPlacement ? (props) => <Popper {...props} placement={popperPlacement} /> : undefined}
              filterOptions={(d) => d}
              ref={this._domRef}
              value={null}
              classes={{
                paper: classes.paper
              }}
              inputValue={text}
              disableClearable
              id={id}
              options={dynOpts.concat(opts)}
              groupBy={(option) => (option.type === 'dynamic' ? '🤖 ' : '📚 ') + option.type.toUpperCase()}
              getOptionLabel={(option) => option.name}
              onChange={this.onSelect}
              onOpen={this.onOpen}
              onClose={this.onClose}
              renderInput={(params) => (
                <TextField
                  onChange={this.onChange}
                  {...params}
                  label={label}
                  placeholder={placeholder}
                  variant="outlined"
                  className={classes.textfield}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    )
                  }}
                />
              )}
            />
          </Tooltip>
        </div>
      </ClickAwayListener>
    );
  }
}

GeoAutocomplete.propTypes = {
  // essentials
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,

  // data
  placeholder: PropTypes.string,
  label: PropTypes.string,
  withDynamicSearch: PropTypes.bool,
  withLatLonSearch: PropTypes.bool,
  popperPlacement: PropTypes.string,

  // callbacks
  dataFn: PropTypes.func.isRequired,
  dataFnMinLength: PropTypes.number,
  onSelect: PropTypes.func.isRequired,

  onOpen: PropTypes.func,
  onClose: PropTypes.func
};

GeoAutocomplete.defaultProps = {
  placeholder: '☕ Starbucks',
  label: 'Search',
  withDynamicSearch: true,
  withLatLonSearch: false,
  dataFnMinLength: 2
};

export default withTheme(withStyles(styles)(GeoAutocomplete));
